/**
 * Who has a login into this household, and what each of them may change (#1344).
 *
 * ⚠️ Shown to everybody, not just the owner. Knowing who else is in your own
 * household is not a privilege, and hiding it would make the roles feel
 * arbitrary — "why can't I do that" is a worse question when you cannot see the
 * answer.
 *
 * ⛔ The controls below only appear for somebody who may use them, but that is a
 * courtesy. The server refuses the request either way; a hidden button is not a
 * permission.
 */
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";

interface RoleOption { value: string; label: string; detail: string }
interface Member {
  id: number;
  userId: number;
  role: string;
  eater: { id: number; name: string } | null;
  joinedAt: string;
}
interface MembersView {
  you: { userId: number; role: string; can: string[] };
  owner: { userId: number | null; role: string };
  members: Member[];
  roles: RoleOption[];
}

export function Members() {
  const qc = useQueryClient();
  const [busy, setBusy] = useState<number | null>(null);
  const q = useQuery<MembersView>({ queryKey: ["members"], queryFn: () => api("/members") });

  const setRole = useMutation({
    mutationFn: ({ id, role }: { id: number; role: string }) =>
      api(`/members/${id}`, { method: "PATCH", body: JSON.stringify({ role }) }),
    onSettled: () => { setBusy(null); qc.invalidateQueries({ queryKey: ["members"] }); },
  });

  const remove = useMutation({
    mutationFn: (id: number) => api(`/members/${id}`, { method: "DELETE" }),
    onSettled: () => { setBusy(null); qc.invalidateQueries({ queryKey: ["members"] }); },
  });

  if (q.isLoading || !q.data) return null;
  const { you, owner, members, roles } = q.data;
  const mayManage = you.can.includes("members");

  return (
    <section className="border-line rounded-xl border p-5">
      <h2 className="font-semibold">Who can sign in</h2>
      <p className="text-muted mt-1 text-sm">
        Everybody here sees the plan, every recipe and the shopping list. What
        differs is what they can change.
      </p>

      <div className="mt-4 flex flex-col gap-2">
        {/* The owner has no member row — they are the account. Shown first so
            the list never looks like it is missing somebody. */}
        <div className="border-line flex items-center justify-between gap-3 rounded-lg border p-3">
          <div>
            <p className="text-sm font-medium">
              You{owner.userId === you.userId ? "" : "r account owner"}
              {owner.userId === you.userId && you.role === "owner" ? "" : ""}
            </p>
            <p className="text-muted text-xs">Owner · runs the account and the subscription</p>
          </div>
        </div>

        {members.length === 0 && (
          <p className="text-muted text-sm">
            Nobody else has their own login yet. Anyone you have sent a taste link
            to can make one from that link — no invite needed from you.
          </p>
        )}

        {members.map((m) => (
          <div key={m.id} className="border-line rounded-lg border p-3">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <p className="text-sm font-medium">{m.eater?.name ?? `Member ${m.userId}`}</p>
              {!mayManage && (
                <span className="text-muted text-xs capitalize">{m.role}</span>
              )}
            </div>

            {mayManage ? (
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                {roles.map((r) => (
                  <button
                    key={r.value}
                    onClick={() => { setBusy(m.id); setRole.mutate({ id: m.id, role: r.value }); }}
                    disabled={busy === m.id}
                    aria-pressed={m.role === r.value}
                    title={r.detail}
                    className="border-line rounded-lg border px-2.5 py-1 text-xs font-medium disabled:opacity-40"
                    style={
                      m.role === r.value
                        ? { borderColor: "var(--color-plate-b)", color: "var(--color-plate-b)" }
                        : undefined
                    }
                  >
                    {r.label}
                  </button>
                ))}
                <button
                  onClick={() => { setBusy(m.id); remove.mutate(m.id); }}
                  disabled={busy === m.id}
                  className="text-muted ml-auto text-xs underline underline-offset-4 disabled:opacity-40"
                >
                  Remove login
                </button>
              </div>
            ) : (
              <p className="text-muted mt-1 text-xs">
                {roles.find((r) => r.value === m.role)?.detail ?? ""}
              </p>
            )}
          </div>
        ))}
      </div>

      {mayManage && members.length > 0 && (
        // ⚠️ Said plainly, because it is the thing somebody will worry about
        // before tapping it.
        <p className="text-muted mt-3 text-xs">
          Removing a login does not remove the person. They still eat here, and
          meals are still planned around what they can and cannot have.
        </p>
      )}
    </section>
  );
}
